import { useCallback, useState } from 'react';
import { toApiError } from '../../api/errors';
import type { Id } from '../../api/types';
import { createVersionBody, type VersionDraft } from './version-form';
import { needsConfirmation, type VersionAction } from './version-actions';
import {
  archiveVersion,
  createVersion,
  releaseVersion,
  unarchiveVersion,
  type Version,
} from './versions-api';

/**
 * PH-28's WRITE HALF — create, release, archive, unarchive.
 *
 * ⛔ THE ERROR IS THE API'S OWN WORDS — `reason` first, exactly as `useSprintEditor`,
 * `useCompleteSprint` and `useRankReorder` do it. For these endpoints that is:
 *   · `a version named 'v1.0.35' already exists in this project`            (409, the common one)
 *   · `version 'v1.0.35' is already released`                               (409)
 *   · `version 'v1.0.35' is archived; unarchive it before releasing`        (409)
 *   · `version 'v1.0.35' is already archived` / `… is not archived`         (409)
 *   · `only an active member of this project may modify its versions`       (403)
 *   · `the request body or query was not valid`                             (400, zod)
 * Every one of those says something "Could not save the version" does not, and the first tells the
 * reader the one thing they need in order to succeed on the second attempt.
 *
 * ⭐ THE OUTCOME IS READ OFF THE SERVER'S ROW, NEVER PREDICTED. `unarchive` is why: its destination
 * is DERIVED from `release_date`, so unarchiving a released version returns `released` — verified in
 * production. A hook that assumed `unreleased` would announce the wrong state and the list would
 * disagree with its own success message one refresh later.
 *
 * ⭐ ONE BUSY KEY, NOT A BOOLEAN. Several versions are on screen at once and each has its own
 * buttons; a single `busy` would grey out the whole page for one row's request.
 */

export interface VersionActionsState {
  /** The version awaiting a Release confirmation, or `null`. */
  releasing: Version | null;
  /** Whether the create form is open. */
  creating: boolean;
  /** The version id (or `'new'`) whose request is in flight. */
  busyId: Id | null;
  /** The API's refusal for the dialog that is open. */
  dialogError: string | null;
  /** Per-row refusals, keyed by version id — an inline action has no dialog to show one in. */
  rowErrors: Record<string, string>;
  /** What happened, once something succeeded. */
  outcome: string | null;
  openCreate: () => void;
  cancelCreate: () => void;
  submitCreate: (draft: VersionDraft) => void;
  /** Starts an action: Release opens its confirmation, the reversible ones run immediately. */
  start: (action: VersionAction, version: Version) => void;
  cancelRelease: () => void;
  confirmRelease: (releaseDate: string) => void;
  dismissOutcome: () => void;
}

export function useVersionActions(
  projectId: Id | null,
  onChanged: () => void,
): VersionActionsState {
  const [releasing, setReleasing] = useState<Version | null>(null);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<Id | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [outcome, setOutcome] = useState<string | null>(null);

  const clearRowError = useCallback((id: Id) => {
    setRowErrors((previous) => {
      const next = { ...previous };
      delete next[String(id)];
      return next;
    });
  }, []);

  const openCreate = useCallback(() => {
    // ⚠️ Belt and braces, and NOT claimed as a tested path: the button that calls this renders only
    //    inside a loaded version list, which cannot exist without a project.
    if (!projectId) return;
    setCreating(true);
    setDialogError(null);
    setOutcome(null);
  }, [projectId]);

  /**
   * ⛔ Not while a request is in flight — the same reasoning as `useSprintEditor.cancel`: if the
   * dialog is dismissed mid-write, a FAILURE has nowhere to appear and the refusal is silent.
   * ⚠️ MEASURED, NOT ASSUMED: breaking this guard ALONE does not turn the suite red, because Cancel
   * carries `disabled={busy}` and the path is unreachable through the UI. The `disabled` attribute is
   * the line under test; this is the second line behind it.
   */
  const cancelCreate = useCallback(() => {
    setBusyId((inFlight) => {
      if (inFlight === null) {
        setCreating(false);
        setDialogError(null);
      }
      return inFlight;
    });
  }, []);

  const cancelRelease = useCallback(() => {
    setBusyId((inFlight) => {
      if (inFlight === null) {
        setReleasing(null);
        setDialogError(null);
      }
      return inFlight;
    });
  }, []);

  const submitCreate = useCallback(
    (draft: VersionDraft) => {
      if (!projectId || busyId !== null) return;
      setBusyId('new');
      setDialogError(null);

      void (async () => {
        try {
          const version = await createVersion(projectId, createVersionBody(draft));
          setCreating(false);
          // ⭐ The SERVER's row, and its state said out loud — `unreleased` is the only state the API
          //   will create, and the reader's next action depends on knowing that.
          setOutcome(
            `Created ${version.name}. It is ${version.state} — press Release on it when it ships.`,
          );
          onChanged();
        } catch (caught) {
          const apiError = toApiError(caught, `/projects/${projectId}/versions`);
          setDialogError(apiError.reason ?? apiError.message);
        } finally {
          setBusyId(null);
        }
      })();
    },
    [projectId, busyId, onChanged],
  );

  const confirmRelease = useCallback(
    (releaseDate: string) => {
      const version = releasing;
      if (!version || busyId !== null) return;
      setBusyId(version.id);
      setDialogError(null);

      void (async () => {
        try {
          const released = await releaseVersion(version.id, releaseDate);
          setReleasing(null);
          setOutcome(`${released.name} is released, dated ${released.release_date ?? releaseDate}.`);
          onChanged();
        } catch (caught) {
          const apiError = toApiError(caught, `/versions/${version.id}/release`);
          setDialogError(apiError.reason ?? apiError.message);
        } finally {
          setBusyId(null);
        }
      })();
    },
    [releasing, busyId, onChanged],
  );

  const start = useCallback(
    (action: VersionAction, version: Version) => {
      if (busyId !== null) return;

      /**
       * 🔴 THE ONLY ACTION THAT ASKS FIRST. Archive and unarchive are each other's undo, so a
       * confirmation on them would be noise — and a confirmation people click through by habit is
       * how the one that MATTERS gets clicked through too.
       */
      if (needsConfirmation(action)) {
        setReleasing(version);
        setDialogError(null);
        setOutcome(null);
        return;
      }

      setBusyId(version.id);
      clearRowError(version.id);
      setOutcome(null);

      void (async () => {
        const path = `/versions/${version.id}/${action}`;
        try {
          const updated =
            action === 'archive' ? await archiveVersion(version.id) : await unarchiveVersion(version.id);
          // ⭐⭐ `updated.state` FROM THE SERVER. Unarchive returns `released` for anything that had
          //   shipped, so announcing "unreleased" here would be a confident wrong answer that the
          //   very next render contradicts.
          setOutcome(
            action === 'archive'
              ? `${updated.name} is archived. Unarchive puts it back — its tickets are untouched.`
              : `${updated.name} is back, as ${updated.state}.`,
          );
          onChanged();
        } catch (caught) {
          const apiError = toApiError(caught, path);
          setRowErrors((previous) => ({
            ...previous,
            [String(version.id)]: apiError.reason ?? apiError.message,
          }));
        } finally {
          setBusyId(null);
        }
      })();
    },
    [busyId, clearRowError, onChanged],
  );

  return {
    releasing,
    creating,
    busyId,
    dialogError,
    rowErrors,
    outcome,
    openCreate,
    cancelCreate,
    submitCreate,
    start,
    cancelRelease,
    confirmRelease,
    dismissOutcome: useCallback(() => setOutcome(null), []),
  };
}
